import { useMutation } from "@tanstack/react-query";
import FileSaver from "file-saver";
import {
  getAdminRaffleRoundsPublicIdExportPath,
  getApiUrl,
  type RaffleRoundDto,
} from "Infrastructure/Api/Api";
import { JWT_KEY } from "Utils/AuthUtils";
import { getCzechDateString } from "Utils/DateUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";

/** 409: the draw sheet no longer matches the checksum sealed at close. */
export class RaffleExportIntegrityError extends Error {}

// Mirrors the BE's Content-Disposition name, which CORS does not expose.
const getExportFileName = ({ region, closedAt, publicID }: RaffleRoundDto) => {
  const closeDay = closedAt
    ? `-${getCzechDateString(new Date(closedAt)).replaceAll("-", "")}`
    : "";

  return `slosovani-${region}${closeDay}-${publicID.replaceAll("-", "")}.csv`;
};

/** The draw sheet is served only while it matches its checksum: success is the integrity check. */
export const useDownloadRaffleExportMutation = () =>
  useMutation({
    mutationFn: async (round: RaffleRoundDto) => {
      const token = getLocalStorageItem(JWT_KEY);
      if (!token) {
        throw new Error("Authentication required for export");
      }

      const response = await fetch(
        `${getApiUrl()}${getAdminRaffleRoundsPublicIdExportPath(round.publicID)}`,
        {
          method: "GET",
          mode: "cors",
          headers: { Authorization: token },
        },
      );

      if (response.status === 409) {
        throw new RaffleExportIntegrityError();
      }
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      FileSaver.saveAs(await response.blob(), getExportFileName(round));
    },
  });
